import React, { memo } from "react";
import { IconSearch } from "@tabler/icons-react";
import MultiSelectDropdown, { DropdownOption } from "./MultiSelectDropdown";
import ColumnVisibilityDropdown, { ColumnConfig } from "./ColumnVisibilityDropdown";
import { DEVICE_TYPE_OPTIONS } from "const/deviceManagement.const";
import { useTranslation } from "react-i18next";

interface FilterBarProps {
  searchText: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedTypes: string[];
  onTypesChange: (selectedValues: string[]) => void;
  selectedOS: string[];
  onOSChange: (selectedValues: string[]) => void;
  filteredOSOptions: DropdownOption[];
  selectedVersions: string[];
  onVersionsChange: (selectedValues: string[]) => void;
  versionOptions: DropdownOption[];
  onResetAll: () => void;
  columns: ColumnConfig[];
  onColumnToggle: (key: string, visible: boolean) => void;
  onResetColumns: () => void;
}

/**
 * Filter bar with search input, multi-select dropdowns, and column toggle dropdown.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  searchText,
  onSearchChange,
  selectedTypes,
  onTypesChange,
  selectedOS,
  onOSChange,
  filteredOSOptions,
  selectedVersions,
  onVersionsChange,
  versionOptions,
  onResetAll,
  columns,
  onColumnToggle,
  onResetColumns,
}) => {
  const { t } = useTranslation("deviceManagement");

  return (
    <div className="d-flex flex-wrap align-items-center gap-3">
      {/* Search input */}
      <div className="input-icon" style={{ minWidth: "260px", flex: 1, maxWidth: "400px" }}>
        <input
          type="text"
          className="form-control"
          placeholder={t("TXT_DEVICE_SEARCH_PLACEHOLDER")}
          value={searchText}
          onChange={onSearchChange}
        />
        <span className="input-icon-addon">
          <IconSearch size={16} />
        </span>
      </div>

      {/* Multi-select dropdowns */}
      <MultiSelectDropdown
        label={t("TXT_DEVICE_FILTER_TYPE")}
        options={DEVICE_TYPE_OPTIONS}
        selectedValues={selectedTypes}
        onChange={onTypesChange}
      />

      <MultiSelectDropdown
        label={t("TXT_DEVICE_FILTER_OS")}
        options={filteredOSOptions}
        selectedValues={selectedOS}
        onChange={onOSChange}
      />

      <MultiSelectDropdown
        label={t("TXT_DEVICE_FILTER_VERSION")}
        options={versionOptions}
        selectedValues={selectedVersions}
        onChange={onVersionsChange}
        hasSearch={true}
        allowCustom={true}
      />

      {/* Column visibility toggle */}
      <div className="ms-auto">
        <ColumnVisibilityDropdown
          columns={columns}
          onChange={onColumnToggle}
          onReset={onResetColumns}
        />
      </div>
    </div>
  );
};

export default memo(FilterBar);
